import React from 'react';

import SelectorCopy from '../../common/selectorCopy';
import { Card, Badge, Country } from '../../common/ui';
import { controlRole } from '../../common/actions/controlRole';
import { returnNationality } from '../../common/actions/returnNationality';
import { SUPER_ADMIN } from '../../config';
import { isEmpty } from '../../utility';

const formatDate = (date) => date ? new Date(date).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// iniziali per l'avatar: nome e cognome se presenti, altrimenti la prima lettera dell'email
function initials(name, surname, email) {
    const fromName = `${(name || '').trim().charAt(0)}${(surname || '').trim().charAt(0)}`;
    return (fromName || (email || '?').charAt(0)).toUpperCase();
}

function Address({ title, address }) {
    if (isEmpty(address)) {
        return (
            <div className="udAddress">
                <h4>{title}</h4>
                <p className="uiMuted">Nessun indirizzo predefinito</p>
            </div>
        );
    }

    const cityLine = [address.cap, address.city, address.province ? `(${address.province})` : ''].filter(Boolean).join(' ');

    return (
        <div className="udAddress">
            <h4>{title}</h4>
            <p>
                <b>{[address.name, address.surname].filter(Boolean).join(' ') || '—'}</b><br />
                {address.firstAddress}{address.secondAddress ? `, ${address.secondAddress}` : ''}<br />
                {cityLine}{address.country ? ` · ${returnNationality(address.country) || address.country}` : ''}
                {address.telephone && <><br /><span className="uiMuted">Tel. {address.telephone}</span></>}
                {address.extraInfo && <><br /><span className="uiMuted">{address.extraInfo}</span></>}
            </p>
        </div>
    );
}

/**
 * Intestazione del dettaglio utente: identità, stato, azioni, dati principali e indirizzi.
 * Sostituisce il vecchio pannello grigio laterale.
 */
export default function UserHeader({ userInDetail, userId, billingAddress, shippingAddress, loading, role, currentUser, infoNotify, openModalMail, openModalUserInDetail }) {
    if (loading) {
        return (
            <Card className="udHeader">
                <div className="udProfile">
                    <div className="udAvatar udSkeleton" />
                    <div className="udIdentity">
                        <div className="udSkeleton" style={{ width: 220, height: 24 }} />
                        <div className="udSkeleton" style={{ width: 280, height: 16, marginTop: 8 }} />
                    </div>
                </div>
                <div className="udSkeleton" style={{ height: 72, marginTop: 16 }} />
            </Card>
        );
    }

    const email = userInDetail.email || userInDetail.id || userId;
    const fullName = [billingAddress.name, billingAddress.surname].filter(Boolean).join(' ');
    const groups = isEmpty(userInDetail.group) ? [] : userInDetail.group;

    // azioni disponibili in base ai permessi
    const can = {
        mail: controlRole(role.support, "api/Support/BackOfficeSendMail", "POST"),
        resetPassword: controlRole(role.user, "api/User/ResetPassword", "PUT"),
        resetTfa: controlRole(role.user, "api/User/ResetTfa", "PUT") && userInDetail.tfa,
        address: controlRole(role.user, "api/User/Address", "PUT"),
        assignRole: currentUser.role === SUPER_ADMIN,
        assignGroup: controlRole(role.user, "api/User/Group/AssignGroup", "PATCH"),
        reseller: controlRole(role.user, "api/User", "PUT"),
        delete: controlRole(role.user, "api/User", "DELETE"),
    };

    return (
        <Card className="udHeader">
            <div className="udProfile">
                <div className="udAvatar" aria-hidden="true">{initials(billingAddress.name, billingAddress.surname, email)}</div>

                <div className="udIdentity">
                    <h2>{fullName || email}</h2>
                    <div className="udEmail">
                        {/* se manca il nome il titolo è già l'email: resta solo il pulsante copia */}
                        {fullName && <span>{email}</span>}
                        <SelectorCopy item={email} infoNotify={infoNotify} noLabel />
                    </div>

                    <div className="udBadges">
                        {userInDetail.isRegistered ? <Badge variant="success">Registrato</Badge> : <Badge variant="danger">Non registrato</Badge>}
                        {userInDetail.isReseller && <Badge>Reseller</Badge>}
                        {userInDetail.tfa && <Badge variant="success">TFA attivo</Badge>}
                        {userInDetail.role && <Badge variant="neutral">Ruolo: {userInDetail.role}</Badge>}
                    </div>
                </div>

                <div className="uiActions udActions">
                    {
                        can.mail &&
                        <button type="button" className="btn btn-sm btn-primary" onClick={openModalMail}>
                            <i className="fas fa-paper-plane" aria-hidden="true" /> Invia mail
                        </button>
                    }
                    {
                        can.resetPassword &&
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openModalUserInDetail("resetPassword")}>
                            <i className="fas fa-key" aria-hidden="true" /> Reset password
                        </button>
                    }
                    {
                        can.resetTfa &&
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openModalUserInDetail("resetTfa")}>
                            <i className="fas fa-shield-alt" aria-hidden="true" /> Reset TFA
                        </button>
                    }
                    {
                        can.address &&
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openModalUserInDetail("editAddress")}>
                            <i className="fas fa-address-card" aria-hidden="true" /> Modifica indirizzi
                        </button>
                    }
                    {
                        can.assignRole &&
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openModalUserInDetail("assignRole")}>
                            <i className="fas fa-address-book" aria-hidden="true" /> Assegna ruolo
                        </button>
                    }
                    {
                        can.assignGroup &&
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openModalUserInDetail("assignGroup")}>
                            <i className="fas fa-users-cog" aria-hidden="true" /> Assegna gruppo
                        </button>
                    }
                    {
                        can.reseller &&
                        <button type="button" className={`btn btn-sm ${userInDetail.isReseller ? 'btn-outline-warning' : 'btn-outline-secondary'}`} onClick={() => openModalUserInDetail("editUser")}>
                            <i className="fas fa-store" aria-hidden="true" /> {userInDetail.isReseller ? 'Rimuovi da reseller' : 'Rendi reseller'}
                        </button>
                    }
                    {
                        can.delete &&
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => openModalUserInDetail("deleteUser")}>
                            <i className="fas fa-user-times" aria-hidden="true" /> Elimina utente
                        </button>
                    }
                </div>
            </div>

            <dl className="udInfo">
                <div>
                    <dt>Paese</dt>
                    <dd>{billingAddress.country ? <Country code={billingAddress.country} /> : '—'}</dd>
                </div>
                <div>
                    <dt>Creato il</dt>
                    <dd>{formatDate(userInDetail.createdAt)}</dd>
                </div>
                <div>
                    <dt>Ultimo accesso</dt>
                    <dd>{formatDate(userInDetail.lastLogOn)}</dd>
                </div>
                <div>
                    <dt>IP registrazione</dt>
                    <dd>
                        {
                            userInDetail.registrationIp ?
                                <span className="udCopy"><span className="uiMono">{userInDetail.registrationIp}</span><SelectorCopy item={userInDetail.registrationIp} infoNotify={infoNotify} noLabel /></span>
                                : '—'
                        }
                    </dd>
                </div>
                <div className="udInfoWide">
                    <dt>Gruppi</dt>
                    <dd>
                        {
                            groups.length ?
                                <span className="udChips">{groups.map(el => <span className="udChip" key={el.name}>{el.name}</span>)}</span>
                                : <span className="uiMuted">Nessun gruppo</span>
                        }
                    </dd>
                </div>
            </dl>

            {
                userInDetail.isRegistered &&
                <div className="udAddresses">
                    <Address title="Indirizzo di fatturazione" address={billingAddress} />
                    <Address title="Indirizzo di spedizione" address={shippingAddress} />
                </div>
            }
        </Card>
    );
}
